import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DeleteOutline,
  Edit,
  Visibility,
  SearchOutlined,
  AddCircleOutline,
  ArticleOutlined,
  DescriptionOutlined,
  CalendarTodayOutlined,
  TrendingUp,
} from '@mui/icons-material';

const Content = () => {
  const [content, setContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [stats, setStats] = useState({
    totalContent: 0,
    recentContent: 0,
  });
  const server_url = process.env.REACT_APP_API_URI;

  useEffect(() => {
    fetch(`${server_url}/content/get-content`)
      .then((response) => response.json())
      .then((data) => {
        console.log('API Response:', data);
        if (data.data && Array.isArray(data.data)) {
          const contentData = data.data;
          setContent(contentData);
          setFilteredContent(contentData);

          // Calculate stats
          const now = new Date();
          const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));
          const recentCount = contentData.filter(
            (c) => new Date(c.createdAt) > thirtyDaysAgo,
          ).length;

          setStats({
            totalContent: contentData.length,
            recentContent: recentCount,
          });
        } else {
          throw new Error('Invalid data format');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setError('Failed to fetch Content.');
        setLoading(false);
      });
  }, [server_url]);

  useEffect(() => {
    let filtered = [...content];

    if (searchTerm) {
      filtered = filtered.filter(
        (c) =>
          c?.mainTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c?.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredContent(filtered);
  }, [searchTerm, content]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this content?')) {
      try {
        const response = await fetch(
          `${server_url}/content/delete-content/${id}`,
          {
            method: 'DELETE',
          },
        );

        if (!response.ok) {
          throw new Error('Failed to delete content.');
        }

        setContent((prevContent) =>
          prevContent.filter((content) => content._id !== id),
        );
      } catch (error) {
        setError('Error deleting content.');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Pagination
  const totalPages = Math.ceil(filteredContent.length / limit);
  const startIndex = (currentPage - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedContent = filteredContent.slice(startIndex, endIndex);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          Content Management
        </h1>
        <p className="text-gray-600">
          Manage and organize your content library
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Content</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.totalContent}
              </h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <ArticleOutlined className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">
                Recent (30 days)
              </p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.recentContent}
              </h3>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <TrendingUp className="text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Add Button */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <Link to="/admin/content/add" className="w-full">
            <button className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg">
              <AddCircleOutline />
              Add Content
            </button>
          </Link>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <label className="font-medium text-gray-700">Show:</label>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[10, 20, 50, 100].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span className="text-gray-600">entries</span>
        </div>

        <div className="text-gray-600">
          Showing {filteredContent.length > 0 ? startIndex + 1 : 0} to{' '}
          {Math.min(endIndex, filteredContent.length)} of{' '}
          {filteredContent.length}{' '}
          {filteredContent.length !== content.length &&
            `(filtered from ${content.length})`}
        </div>
      </div>

      {/* Content Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-red-500 font-medium">{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <tr>
                  <th className="py-4 px-6 text-left font-semibold">Title</th>
                  <th className="py-4 px-6 text-left font-semibold">
                    Description
                  </th>
                  <th className="py-4 px-6 text-left font-semibold">Created</th>
                  <th className="py-4 px-6 text-center font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedContent.length > 0 ? (
                  paginatedContent.map((item) => (
                    <tr
                      key={item._id}
                      className="hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-semibold">
                            <DescriptionOutlined fontSize="small" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800">
                              {item.mainTitle}
                            </p>
                            <p className="text-sm text-gray-500">
                              Content Item
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <p className="text-gray-700 text-sm line-clamp-2">
                          {item.description || 'No description available'}
                        </p>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <CalendarTodayOutlined
                            className="text-gray-400"
                            fontSize="small"
                          />
                          <span className="text-gray-600 text-sm">
                            {formatDate(item.createdAt)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to={`/admin/content/view/${item._id}`}
                            className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                            title="View Content"
                          >
                            <Visibility fontSize="small" />
                          </Link>
                          <Link
                            to={`/admin/content/edit/${item._id}`}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Edit Content"
                          >
                            <Edit fontSize="small" />
                          </Link>
                          <button
                            onClick={() => handleDelete(item._id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete Content"
                          >
                            <DeleteOutline fontSize="small" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <ArticleOutlined className="text-gray-400 text-5xl" />
                        <p className="text-gray-500 text-lg font-medium">
                          {searchTerm
                            ? 'No content matches your search'
                            : 'No content found'}
                        </p>
                        <p className="text-gray-400 text-sm">
                          {searchTerm
                            ? 'Try adjusting your search term'
                            : 'Click "Add Content" to create your first content item'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-md p-4 mt-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Previous
          </button>

          <div className="flex items-center gap-2">
            {[...Array(Math.min(totalPages, 5))].map((_, index) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = index + 1;
              } else if (currentPage <= 3) {
                pageNumber = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + index;
              } else {
                pageNumber = currentPage - 2 + index;
              }

              return (
                <button
                  key={index}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                    currentPage === pageNumber
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Content;
